import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { Listbox } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { CheckIcon, ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, PlusIcon, PencilIcon, EyeIcon } from '@heroicons/react/20/solid';

function Kanban() {
  const { user } = useAuth();
  const [kanbanJobs, setKanbanJobs] = useState([]);
  const [filteredKanbanJobs, setFilteredKanbanJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreatePOModalOpen, setIsCreatePOModalOpen] = useState(false);
  const [isReleasesModalOpen, setIsReleasesModalOpen] = useState(false);
  const [isCreateReleaseModalOpen, setIsCreateReleaseModalOpen] = useState(false);
  const [isLogProductionModalOpen, setIsLogProductionModalOpen] = useState(false);
  const [isLogShipmentModalOpen, setIsLogShipmentModalOpen] = useState(false);
  const [selectedKanbanJob, setSelectedKanbanJob] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [lineItems, setLineItems] = useState([
    { part_number: '', description: '', quantity_ordered: '', unit_price: '', searchTerm: '' }
  ]);
  const [parts, setParts] = useState([]);
  const [shops, setShops] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterShop, setFilterShop] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newReleaseNumber, setNewReleaseNumber] = useState('');
  const [newQuantityOrdered, setNewQuantityOrdered] = useState('');
  const [newReleaseDate, setNewReleaseDate] = useState('');
  const [productionQuantity, setProductionQuantity] = useState('');
  const [shipmentQuantity, setShipmentQuantity] = useState('');

  useEffect(() => {
    document.title = 'Kanban POs';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
    
      setLoading(true);
      try {
        const { data: jobs, error: jobsError } = await supabase
          .from('kanban_jobs')
          .select(`
            id, po_number, po_line_number, part_number, description, status, 
            quantity_on_order, produced_quantity, shipped_quantity, 
            po_start_date, shops(id, name),
            parts!inner(part_number, part_number_master, revision, inventory_quantity)
          `)
          .order('po_number, po_line_number');
        if (jobsError) throw jobsError;
    
        const formattedJobs = jobs.map(job => ({
          id: job.id,
          po_number: job.po_number,
          po_line_number: job.po_line_number,
          part_number: job.part_number,
          description: job.description,
          status: job.status,
          quantity_on_order: job.quantity_on_order,
          produced_quantity: job.produced_quantity,
          shipped_quantity: job.shipped_quantity,
          remaining_quantity: job.quantity_on_order - job.shipped_quantity,
          po_start_date: job.po_start_date,
          shop_name: job.shops?.name || 'Unknown',
          inventory_quantity: job.parts?.inventory_quantity || 0,
        }));
    
        setKanbanJobs(formattedJobs);
        setFilteredKanbanJobs(formattedJobs);
    
        const { data: partsData, error: partsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description');
        if (partsError) throw partsError;
        setParts(partsData || []);
    
        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to fetch Kanban jobs data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { part_number: '', description: '', quantity_ordered: '', unit_price: '', searchTerm: '' }
    ]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const updateLineItemMultiple = (index, updates) => {
    setLineItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, ...updates } : item
      )
    );
  };

  const handlePartSelect = (index, part) => {
    updateLineItemMultiple(index, {
      part_number: part.part_number,
      description: part.description,
      searchTerm: ''
    });
  };

  const handleCreatePart = async (index) => {
    const searchTerm = lineItems[index].searchTerm;
    if (!searchTerm) return;
    const newPart = {
      part_number_master: searchTerm,
      revision: '0',
      description: `New Part: ${searchTerm}`,
      inventory_quantity: 0,
    };
    try {
      const { data, error } = await supabase
        .from('parts')
        .insert(newPart)
        .select('part_number, description')
        .single();
      if (error) throw error;
      setParts([...parts, data]);
      handlePartSelect(index, data);
    } catch (err) {
      console.error('Error creating part:', err);
      setError('Failed to create new part: ' + err.message);
    }
  };

  const handleSavePO = async () => {
    if (
      !poNumber ||
      !poDate ||
      lineItems.some(
        item =>
          !item.part_number ||
          !item.quantity_ordered ||
          !item.unit_price
      )
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    const jobs = lineItems.map((item, index) => ({
      po_number: poNumber,
      po_line_number: index + 1,
      part_number: item.part_number,
      description: item.description,
      quantity_on_order: parseFloat(item.quantity_ordered),
      unit_price: parseFloat(item.unit_price),
      po_start_date: poDate,
      status: 'not_started',
      shop_id: 1,
      customer_id: 1,
      produced_quantity: 0,
      shipped_quantity: 0,
    }));

    try {
      const { error } = await supabase.from('kanban_jobs').insert(jobs);
      if (error) throw error;

      const { data, error: fetchError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          po_start_date, shops(id, name),
          parts!inner(part_number, inventory_quantity)
        `)
        .order('po_number, po_line_number');
      if (fetchError) throw fetchError;

      const formattedJobs = data.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        remaining_quantity: job.quantity_on_order - job.shipped_quantity,
        po_start_date: job.po_start_date,
        shop_name: job.shops?.name || 'Unknown',
        inventory_quantity: job.parts?.inventory_quantity || 0,
      }));

      setKanbanJobs(formattedJobs);
      setFilteredKanbanJobs(formattedJobs);

      setIsCreatePOModalOpen(false);
      setPoNumber('');
      setPoDate('');
      setLineItems([
        { part_number: '', description: '', quantity_ordered: '', unit_price: '', searchTerm: '' }
      ]);
      setError(null);
      toast.success('Kanban Purchase Order created successfully!');
    } catch (err) {
      console.error('Error saving PO:', err);
      setError('Failed to save PO: ' + err.message);
      toast.error(`Failed to save PO: ${err.message}`);
    }
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedJobs = [...filteredKanbanJobs].sort((a, b) => {
      const aValue = a[field] || '';
      const bValue = b[field] || '';
      if (typeof aValue === 'number')
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
    setFilteredKanbanJobs(sortedJobs);
  };

  const handleFilter = () => {
    let filtered = [...kanbanJobs];
    if (filterStatus.length > 0) {
      filtered = filtered.filter(job => filterStatus.includes(job.status));
    }
    if (filterShop) {
      filtered = filtered.filter(job => job.shop_name === filterShop);
    }
    if (searchQuery) {
      filtered = filtered.filter(job =>
        job.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.part_number.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredKanbanJobs(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [filterStatus, filterShop, searchQuery]);

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'not_started': return 'bg-red-100 text-red-800';
      case 'awaiting_production': return 'bg-orange-100 text-orange-800';
      case 'in_production': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-green-100 text-green-800';
      case 'partially_shipped': return 'bg-blue-100 text-blue-800';
      case 'ready_for_shipment': return 'bg-purple-100 text-purple-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openReleasesModal = async (kanbanJob) => {
    setSelectedKanbanJob(kanbanJob);
    try {
      const { data, error } = await supabase
        .from('kanban_work_orders')
        .select('*')
        .eq('kanban_job_id', kanbanJob.id)
        .order('release_date', { ascending: true });
      if (error) throw new Error(`Work orders fetch error: ${error.message}`);
      setWorkOrders(data || []);
    } catch (err) {
      console.error('Error fetching work orders:', err.message);
      toast.error(`Failed to fetch work orders: ${err.message}`);
      setWorkOrders([]);
    }
    setIsReleasesModalOpen(true);
  };

  const closeReleasesModal = () => {
    setIsReleasesModalOpen(false);
    setSelectedKanbanJob(null);
    setWorkOrders([]);
  };

  const openCreateReleaseModal = () => {
    setNewReleaseNumber('');
    setNewQuantityOrdered('');
    setNewReleaseDate('');
    setIsCreateReleaseModalOpen(true);
  };

  const closeCreateReleaseModal = () => {
    setIsCreateReleaseModalOpen(false);
    setNewReleaseNumber('');
    setNewQuantityOrdered('');
    setNewReleaseDate('');
  };

  const handleCreateRelease = async () => {
    if (!newReleaseNumber || !newQuantityOrdered || newQuantityOrdered <= 0 || !newReleaseDate) {
      toast.error('Please fill in all fields with valid values.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('kanban_work_orders')
        .insert({
          kanban_job_id: selectedKanbanJob.id,
          release_number: newReleaseNumber,
          quantity_ordered: parseInt(newQuantityOrdered),
          produced_quantity: 0,
          shipped_quantity: 0,
          release_date: newReleaseDate,
          status: 'not_started',
        })
        .select()
        .single();
      if (error) throw new Error(`Failed to create work order: ${error.message}`);

      setWorkOrders([...workOrders, data]);
      closeCreateReleaseModal();
      toast.success('Work order created successfully!');
    } catch (err) {
      console.error('Error creating work order:', err);
      toast.error(`Failed to create work order: ${err.message}`);
    }
  };

  const openLogProductionModal = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setProductionQuantity('');
    setIsLogProductionModalOpen(true);
  };

  const closeLogProductionModal = () => {
    setIsLogProductionModalOpen(false);
    setSelectedWorkOrder(null);
    setProductionQuantity('');
  };

  const handleLogProduction = async () => {
    if (!productionQuantity || productionQuantity <= 0) {
      toast.error('Please enter a valid production quantity.');
      return;
    }

    const newProducedQuantity = selectedWorkOrder.produced_quantity + parseInt(productionQuantity);
    if (newProducedQuantity > selectedWorkOrder.quantity_ordered) {
      toast.error('Produced quantity cannot exceed ordered quantity.');
      return;
    }

    try {
      // Update the work order
      const { error: workOrderError } = await supabase
        .from('kanban_work_orders')
        .update({
          produced_quantity: newProducedQuantity,
          status: newProducedQuantity === selectedWorkOrder.quantity_ordered ? 'ready_for_shipment' : selectedWorkOrder.status,
        })
        .eq('id', selectedWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      // Update the parent Kanban job
      const { data: kanbanJob, error: kanbanError } = await supabase
        .from('kanban_jobs')
        .select('produced_quantity')
        .eq('id', selectedWorkOrder.kanban_job_id)
        .single();
      if (kanbanError) throw new Error(`Failed to fetch Kanban job: ${kanbanError.message}`);

      const newKanbanProducedQuantity = kanbanJob.produced_quantity + parseInt(productionQuantity);
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          produced_quantity: newKanbanProducedQuantity,
        })
        .eq('id', selectedWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      // Update state
      setWorkOrders(workOrders.map(wo =>
        wo.id === selectedWorkOrder.id
          ? { ...wo, produced_quantity: newProducedQuantity }
          : wo
      ));
      setKanbanJobs(kanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, produced_quantity: newKanbanProducedQuantity }
          : job
      ));
      setFilteredKanbanJobs(filteredKanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, produced_quantity: newKanbanProducedQuantity }
          : job
      ));

      closeLogProductionModal();
      toast.success('Production logged successfully!');
    } catch (err) {
      console.error('Error logging production:', err);
      toast.error(`Failed to log production: ${err.message}`);
    }
  };

  const openLogShipmentModal = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShipmentQuantity('');
    setIsLogShipmentModalOpen(true);
  };

  const closeLogShipmentModal = () => {
    setIsLogShipmentModalOpen(false);
    setSelectedWorkOrder(null);
    setShipmentQuantity('');
  };

  const handleLogShipment = async () => {
    if (!shipmentQuantity || shipmentQuantity <= 0) {
      toast.error('Please enter a valid shipment quantity.');
      return;
    }

    const newShippedQuantity = selectedWorkOrder.shipped_quantity + parseInt(shipmentQuantity);
    if (newShippedQuantity > selectedWorkOrder.quantity_ordered) {
      toast.error('Shipped quantity cannot exceed ordered quantity.');
      return;
    }
    if (newShippedQuantity > selectedWorkOrder.produced_quantity) {
      toast.error('Shipped quantity cannot exceed produced quantity.');
      return;
    }

    try {
      // Update the work order
      const { error: workOrderError } = await supabase
        .from('kanban_work_orders')
        .update({
          shipped_quantity: newShippedQuantity,
          status: newShippedQuantity === selectedWorkOrder.quantity_ordered ? 'closed' : 'partially_shipped',
        })
        .eq('id', selectedWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      // Update the parent Kanban job
      const { data: kanbanJob, error: kanbanError } = await supabase
        .from('kanban_jobs')
        .select('shipped_quantity')
        .eq('id', selectedWorkOrder.kanban_job_id)
        .single();
      if (kanbanError) throw new Error(`Failed to fetch Kanban job: ${kanbanError.message}`);

      const newKanbanShippedQuantity = kanbanJob.shipped_quantity + parseInt(shipmentQuantity);
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          shipped_quantity: newKanbanShippedQuantity,
        })
        .eq('id', selectedWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      // Update state
      setWorkOrders(workOrders.map(wo =>
        wo.id === selectedWorkOrder.id
          ? { ...wo, shipped_quantity: newShippedQuantity }
          : wo
      ));
      setKanbanJobs(kanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, shipped_quantity: newKanbanShippedQuantity }
          : job
      ));
      setFilteredKanbanJobs(filteredKanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, shipped_quantity: newKanbanShippedQuantity }
          : job
      ));

      closeLogShipmentModal();
      toast.success('Shipment logged successfully!');
    } catch (err) {
      console.error('Error logging shipment:', err);
      toast.error(`Failed to log shipment: ${err.message}`);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Kanban POs</h1>
          <button
            onClick={() => setIsCreatePOModalOpen(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
          >
            Create Kanban PO
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border-2 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by PO number or Part number"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Shop</label>
              <select
                value={filterShop}
                onChange={(e) => setFilterShop(e.target.value)}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All</option>
                {shops.map(shop => (
                  <option key={shop.id} value={shop.name}>{shop.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Kanban Jobs Table */}
        <div className="bg-white rounded-lg border-2 overflow-x-scroll">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('po_number')}
        >
          PO Number {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('po_line_number')}
        >
          Line {sortField === 'po_line_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('part_number')}
        >
          Part {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fulfillment</th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory</th>
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('quantity_on_order')}
        >
          Qty {sortField === 'quantity_on_order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('shipped_quantity')}
        >
          Shipped {sortField === 'shipped_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('inventory_quantity')}
        >
          Inventory {sortField === 'inventory_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        
        <th
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('shop_name')}
        >
          Shop {sortField === 'shop_name' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {filteredKanbanJobs.map((job) => (
        <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <Link to={`/kanban/${job.id}`} className="text-indigo-600 hover:text-indigo-800 transition duration-150">
              {job.po_number}
            </Link>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.po_line_number}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.part_number}</td>
          <td
            className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs"
            title={job.description}
          >
            {job.description?.length > 10
              ? job.description.slice(0, 10) + '...'
              : job.description}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div className="w-24">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full"
                  style={{
                    width: `${
                      (job.shipped_quantity / job.quantity_on_order) * 100 || 0
                    }%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {Math.round((job.shipped_quantity / job.quantity_on_order) * 100) || 0}%
              </p>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div className="w-24">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{
                    width: `${
                      (job.inventory_quantity / job.quantity_on_order) * 100 || 0
                    }%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {Math.round((job.inventory_quantity / job.quantity_on_order) * 100) || 0}%
              </p>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.quantity_on_order}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shipped_quantity}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.inventory_quantity}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shop_name}</td>
          
        </tr>
      ))}
    </tbody>
  </table>
</div>

        {/* Create Kanban PO Modal */}
        <Transition appear show={isCreatePOModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsCreatePOModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-white p-6 border-2 transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold mb-4 text-gray-900">
                      Create Kanban Purchase Order
                    </Dialog.Title>

                    {error && <p className="mb-4 text-red-500">{error}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">PO Number</label>
                        <input
                          type="text"
                          value={poNumber}
                          onChange={(e) => setPoNumber(e.target.value)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">PO Start</label>
                        <input
                          type="date"
                          value={poDate}
                          onChange={(e) => setPoDate(e.target.value)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Line Items</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 mb-20">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Ordered</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {lineItems.map((item, index) => {
                              const filteredParts = parts.filter(part =>
                                part.part_number
                                  .toLowerCase()
                                  .includes((item.searchTerm || '').toLowerCase())
                              );
                              return (
                                <tr key={index} className="hover:bg-gray-50 transition duration-150 overflow-visible">
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 relative">
                                    <input
                                      type="text"
                                      value={item.searchTerm || item.part_number}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        updateLineItemMultiple(index, {
                                          searchTerm: value,
                                          part_number: value !== item.part_number ? '' : item.part_number,
                                          description: value !== item.part_number ? '' : item.description,
                                        });
                                      }}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Search or enter part number"
                                    />
                                    {item.searchTerm && (
                                      <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                                        {filteredParts.length > 0 ? (
                                          filteredParts.map((part) => (
                                            <div
                                              key={part.part_number}
                                              onClick={() => handlePartSelect(index, part)}
                                              className="px-4 py-2 text-sm text-gray-900 hover:bg-indigo-100 cursor-pointer transition duration-150"
                                            >
                                              {part.part_number} - {part.description}
                                            </div>
                                          ))
                                        ) : (
                                          <div
                                            onClick={() => handleCreatePart(index)}
                                            className="px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-100 cursor-pointer transition duration-150"
                                          >
                                            Create new part: "{item.searchTerm}"
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    <input
                                      type="text"
                                      value={item.description}
                                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Description"
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="number"
                                      value={item.quantity_ordered}
                                      onChange={(e) => updateLineItem(index, 'quantity_ordered', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      min="0"
                                      placeholder="Qty"
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="number"
                                      value={item.unit_price}
                                      onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      min="0"
                                      step="0.01"
                                      placeholder="Price"
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <button
                                      onClick={() => removeLineItem(index)}
                                      className="text-red-600 hover:text-red-800 transition duration-150"
                                      disabled={lineItems.length === 1}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr></tr>
                          </tbody>
                        </table>
                      </div>

                      <button
                        onClick={addLineItem}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                      >
                        Add Line Item
                      </button>
                    </div>

                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={() => setIsCreatePOModalOpen(false)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePO}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                      >
                        Save PO
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Releases Modal */}
        <Transition appear show={isReleasesModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeReleasesModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Releases for PO #{selectedKanbanJob?.po_number} (Line: {selectedKanbanJob?.po_line_number})
                    </Dialog.Title>
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={openCreateReleaseModal}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Create Release
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Release Number</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity Ordered</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Produced</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Shipped</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Release Date</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {workOrders.map((workOrder) => (
                            <tr key={workOrder.id} className="hover:bg-gray-50 transition duration-150">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{workOrder.release_number}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.quantity_ordered}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.produced_quantity}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.shipped_quantity}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(workOrder.release_date).toLocaleDateString()}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.status.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                  onClick={() => openLogProductionModal(workOrder)}
                                  className="text-indigo-600 hover:text-indigo-800 mr-4"
                                >
                                  Log Production
                                </button>
                                <button
                                  onClick={() => openLogShipmentModal(workOrder)}
                                  className="text-indigo-600 hover:text-indigo-800"
                                >
                                  Log Shipment
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={closeReleasesModal}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Create Release Modal */}
        <Transition appear show={isCreateReleaseModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeCreateReleaseModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Create Release for PO #{selectedKanbanJob?.po_number}
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Release Number</label>
                        <input
                          type="text"
                          value={newReleaseNumber}
                          onChange={(e) => setNewReleaseNumber(e.target.value)}
                          placeholder="Enter release number"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Ordered</label>
                        <input
                          type="number"
                          value={newQuantityOrdered}
                          onChange={(e) => setNewQuantityOrdered(e.target.value)}
                          placeholder="Enter quantity ordered"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Release Date</label>
                        <input
                          type="date"
                          value={newReleaseDate}
                          onChange={(e) => setNewReleaseDate(e.target.value)}
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeCreateReleaseModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateRelease}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Create
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Log Production Modal */}
        <Transition appear show={isLogProductionModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeLogProductionModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Log Production for Release {selectedWorkOrder?.release_number}
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Produced</label>
                        <input
                          type="number"
                          value={productionQuantity}
                          onChange={(e) => setProductionQuantity(e.target.value)}
                          placeholder="Enter quantity produced"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeLogProductionModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleLogProduction}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Log Production
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Log Shipment Modal */}
        <Transition appear show={isLogShipmentModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeLogShipmentModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Log Shipment for Release {selectedWorkOrder?.release_number}
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Shipped</label>
                        <input
                          type="number"
                          value={shipmentQuantity}
                          onChange={(e) => setShipmentQuantity(e.target.value)}
                          placeholder="Enter quantity shipped"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeLogShipmentModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleLogShipment}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Log Shipment
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}

export default Kanban;